import { registerSearchEvent } from './ga4/events'
import { get } from 'svelte/store'
import { is_set_array } from './helpers'
import {
	storeActiveFilters,
	storeBrandSearchFilter,
	storeCategorySearchFilter,
	storeMaxPrice,
	storeRecentSearches,
	storeSearchedText,
	storeSearchFilters,
	storeSearchUpdated,
} from './stores'
import type { Suggestion } from './types'
import type { BaseBrand, BrandLink, Category, FullBrand } from './elasticsearch/types'
import type { SiteScope } from '$lib/js/helpers/i18n'

function isCategory(result: FullBrand | Category): result is Category {
	return (result as Category).name !== undefined
}

/**
 * Load search suggested options
 */
export async function loadOptions(value: string, { scope }: { scope: SiteScope }): Promise<Suggestion[]> {
	const response = await fetch(`/${scope}/endpoints/search/autocomplete?q=${encodeURIComponent(value)}`)
	const results: (string | Category | FullBrand)[] = await response.json()

	return results.map(option => {
		let suggestion_obj: Suggestion = { icon: 'edit', name: '', type: '', link: '' }

		if (typeof option !== 'string') {
			if (isCategory(option)) {
				// Category
				// FIXME: this is not sustainable; get the real name somehow
				let name: string = option.url_path.replace('c/', '').split('/').slice(-2)[0]
					.replaceAll('-', ' ').replace('ba o', 'baño').replace('peque o', 'pequeño').replace('pa al', 'pañal')
					.replace('mu ec', 'muñec').replace('ni o', 'niña').replace('ni a', 'niña')
				let full_name = name.charAt(0).toUpperCase() + name.slice(1) + ' > ' + option.name
				let result = full_name ? full_name : option.name
				suggestion_obj.icon = 'categories'
				suggestion_obj.name = result
				suggestion_obj.type = 'category'
				suggestion_obj.obj = option
				// suggestion_obj.link = `/${scope}/${option.url_path}`
			} else {
				// Brand
				suggestion_obj.icon = 'badge'
				suggestion_obj.name = option.page_title
				suggestion_obj.type = 'brand'
				suggestion_obj.link = `/${scope}/brands/${option.slug}`
				suggestion_obj.obj = option
			}
		} else {
			// String
			suggestion_obj.icon = 'eye'
			suggestion_obj.name = option
			suggestion_obj.type = 'term'
			suggestion_obj.link = `/${scope}/catalogsearch/result?q=${option}`
		}

		return suggestion_obj
	})
}


/**
 * Load search suggested options
 */
export async function executeSearch(val: string, { scope }: { scope: SiteScope }) {
	let products: Record<string, unknown>[] = []
	storeActiveFilters.set({})
	let categoryStore: { id: any } | null = get(storeCategorySearchFilter)
	let brandStore = get(storeBrandSearchFilter)

	if (val && val.length >= 2 || categoryStore || brandStore) {
		const start_typed_val = val
		let options = {
			search_text: val,
			brand_ids: [] as string[],
			cat_ids: [] as number[],
		}
		if (categoryStore) {
			options.cat_ids.push(categoryStore.id)
		}
		if (brandStore) {
			options.brand_ids.push(brandStore.option_id)
		}
		const [suggestions_result, search_response] = await Promise.all([
			loadOptions(val, {scope: scope}),
			fetch(`/${scope}/endpoints/catalog_search`, {
				method: 'POST',
				body: JSON.stringify(options),
				headers: {
					'Content-Type': 'application/json',
				},
			}),
		])
		const search_result = await search_response.json()

		// JB moved so it works
		// But really don't know what that is for
		storeSearchUpdated.update(n => (n || 0) + 1)

		if (get(storeSearchedText) === start_typed_val) {
			registerSearchEvent(start_typed_val)
			// TODO[i18n]: this 1.21 is not compatible with France
			//   We should do an aggregation on the max_price with tax
			const max_price = parseInt((search_result.products.aggregations.max_price.value * 1.21 + 1).toFixed(0))

			storeSearchFilters.set({
				subcats: search_result.subcats,
				brands: search_result.brands,
				brand_aggs: search_result.brand_aggs,
				max_price: max_price,
				prev_filters: get(storeSearchFilters).prev_filters,
				suggestions: suggestions_result,
			})
			storeMaxPrice.set(max_price)

			// Store recent searches
			products = search_result.products.hits?.hits?.map((doc: { _source: Record<string, unknown> }) => doc._source)

			if (is_set_array(products) && products.length > 0) {
				if (!get(storeRecentSearches).includes(start_typed_val)) {
					get(storeRecentSearches).unshift(start_typed_val)
				}

				if (get(storeRecentSearches).length >= 9) {
					storeRecentSearches.set(get(storeRecentSearches).slice(0, 8))
				}
			}

			return {
				products: products,
				options: options,
			}
		}
	} else {
		storeSearchUpdated.set(0)
	}
}

/**
 * Get brands and cats ids for product filtering
 */
export function getCheckedBrandsIDs(brands: (BaseBrand & BrandLink)[]) {
	return brands.filter((brand) => brand.checked == true).map((brand) => brand.option_id)
}

export function getCheckedCatsIDs(subcats: Category[]) {
	return subcats.filter((cat) => cat.checked == true).map((cat) => cat.id)
}
